/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import SendIcon from '@material-ui/icons/Send';
import UndoIcon from '@material-ui/icons/Undo';
import { useTranslation } from 'react-i18next';
import Tiff from 'tiff.js';
import { useMediaQuery } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { callApi } from 'api/helpers/api.helpers';
import CreditCard from '@material-ui/icons/CreditCard';
import draftApi from '../../api/draft.api';
import { Roles } from '../../shared/enums/Roles';
import { SubmissionStatus } from '../../models/SubmissionStatus';
import useAttachments from '../../components/preview/useAttachments';
import { getProcedureName } from '../../procedure/SubmissionProcedure';
import { useUiSchema } from '../../shared/rjsf/useUiSchema';
import { getSubmissionTitle } from '../../shared/submission/submissionPropertyAccessor';
import useProcedure from '../../procedure/useProcedure';
import { useAuth } from '../../redux/features/auth/useAuth';
import DoubleUndo from '../../components/icons/DoubleUndoIcon';
import { showAlertSuccess } from '../../redux/features/alerts/alerts.slice';
import SignIcon from '../../components/icons/SignIcon';
import { RoutePath } from '../../shared/enums/RoutePath';
import { createMessage } from '../../redux/features/alerts/alerts.helper';
import { SignatureType } from '../../redux/enums/SignatureType';
import { selectVersion } from '../../redux/features/configuration/configuration.slice';
import { isConfigurationVersionCorrect } from '../../shared/utils/submissionUtils';

export default function useSubmissionPreview() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const procedure = useProcedure();
  const { userData } = useAuth();

  const [submission, setSubmission] = useState(null);
  const [previewObject, setPreviewObject] = useState(null);
  const [signatureImageUrl, setSignatureImageUrl] = useState(null);
  const [signatureImageFile, setSignatureImageFile] = useState(null);
  const { attachments, attachmentsConfigReady, loadAttachmentFile } = useAttachments(submission);
  const [staticText, setStaticText] = useState(null);
  const [bodyButtonsConfig, setBodyButtonsConfig] = useState([]);
  const [footerButtonsConfig, setFooterButtonsConfig] = useState([]);
  const [isDialogSendSubmissionOpen, setIsDialogSendSubmissionOpen] = useState(false);
  const [isDialogPreviewFacsimileOpen, setIsDialogPreviewFacsimileOpen] = useState(false);
  const { uiSchema } = useUiSchema();
  const [isStatusChangeDialogOpen, setIsStatusChangeDialogOpen] = useState(false);
  const [revertStatusAction, setRevertStatusAction] = useState(null);
  const [fetchingSubmission, setFetchingSubmission] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const configurationVersion = useSelector(selectVersion);

  const isLargeOrBigger = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const location = useLocation();
  const match = useRouteMatch();
  const submissionId = match.params?.submissionId;

  // Due to browsers lack of capability for rendering Tiff images,
  // we transform a TIFF to a PNG image when an error is thrown.
  // TO-DO: Improve this by passing the image type as part of the API call
  // TO-DO: to avoid assumptions on the error cause.
  const handleFacsimileImageError = async (e) => {
    e.persist();

    const buffer = await signatureImageFile.arrayBuffer();
    const tiffObject = new Tiff({ buffer });

    // eslint-disable-next-line no-param-reassign
    e.target.src = tiffObject.toDataURL();
  };

  const openDialogSendSubmission = () => {
    setIsDialogSendSubmissionOpen(true);
  };

  const closeDialogSendSubmission = () => {
    setIsDialogSendSubmissionOpen(false);
  };

  const confirmDialogSendSubmission = async () => {
    setIsLoading(true);
    const response = await callApi(draftApi.sendToFiling)(submissionId);

    if (response && response?.status === 200 && response?.data) {
      dispatch(showAlertSuccess(createMessage(t('submission-sent'))));
    }
    const locationStates = Array.isArray(history.location.state) ? history.location.state : [history.location.state];
    const locationState = locationStates.find(value =>
      [t(procedure.label), t('overview.page')].includes(value?.goBackText),
    );
    setIsDialogSendSubmissionOpen(false);
    setIsLoading(false);

    if (locationState) {
      history.push(locationState?.goBackPath, { showProcessingDialog: true });
    } else {
      history.go(0);
    }
  };

  const openDialogPreviewFacsimile = () => {
    setIsDialogPreviewFacsimileOpen(true);
  };

  const closeDialogPreviewFacsimile = () => {
    setIsDialogPreviewFacsimileOpen(false);
  };

  const fetchSubmission = async () => {
    setFetchingSubmission(true);
    const draftTmp = await callApi(draftApi.getById)(submissionId);
    if (draftTmp.status === SubmissionStatus.READY_TO_SEND.id) {
      setPreviewObject({ id: 'signature', path: null });
    } else {
      const filePath = await callApi(draftApi.openSubmission)(submissionId);
      setPreviewObject({ id: 'submission', path: filePath });
    }

    if (draftTmp?.signature?.signatureType === SignatureType.FACSIMILE.value) {
      const [imageUrl, imageFile] = await callApi(draftApi.openFile)(
        draftTmp.id,
        draftTmp.signature.signatureFileId,
        draftTmp.signature.signatureFileName,
      );
      setSignatureImageUrl(imageUrl);
      setSignatureImageFile(imageFile);

      draftTmp.signature.openFacsimilePreview = openDialogPreviewFacsimile;
    }

    setSubmission(draftTmp);
    setFetchingSubmission(false);
  };

  const handleBackToStatus = async (submissionStatus) => {
    setIsStatusChangeDialogOpen(true);
    setRevertStatusAction(submissionStatus.id);
  };

  const closeStatusChangeDialog = async () => {
    setIsStatusChangeDialogOpen(false);
    setRevertStatusAction(null);
  };

  const handleStatusChangeDialogConfirm = async () => {
    closeStatusChangeDialog();
    fetchSubmission();
  };

  const handleSendToSign = async () => {
    await callApi(draftApi.sendToSign)(submissionId, false);
    fetchSubmission();
  };

  // TO-DO: i18n texts
  const handleSignButton = async (sub) => {
    const locationState = Array.isArray(history?.location?.state)
      ? history?.location?.state
      : [history?.location?.state];

    const signRoutePath = RoutePath.isUmaRoute()
      ? RoutePath.PROCEDURE_NON_ORG_SIGNING_SUBMISSION
      : RoutePath.PROCEDURE_SIGNING_SUBMISSION;
    const params = { procedure: procedure.id, submissionId: sub.id };
    const path = RoutePath.generateRoutePath(signRoutePath, params);
    history.push(
      path,
      [
        ...locationState,
        {
          goBackText: `${t('Preview')} ${getSubmissionTitle(submission, uiSchema)}`,
          goBackPath: `/${procedure.id}/preview/${sub.id}`,
        },
      ].filter(Boolean),
    );
  };

  const handleSendButton = async () => {
    openDialogSendSubmission();
  };

  const handlePayButton = async () => {
    const params = {
      procedure: submission?.procedure,
      submissionId: submission?.id,
      ...(submission?.initialSubmissionProcedure && {
        initialSubmissionProcedure: submission?.initialSubmissionProcedure,
      }),
    };
    history.push(RoutePath.generateRoutePath(RoutePath.PROCEDURE_PAYMENT, params));
  };

  const handleRequestReceiptButton = async () => {
    const params = {
      procedure: submission?.procedure,
      submissionId: submission?.id,
    };
    history.push(RoutePath.generateRoutePath(RoutePath.PROCEDURE_REQUEST_RECEIPT_SUBMISSION, params));
  };

  const sendToSignConfig = isEnabled => [
    {
      text: t('send.to.sign'),
      tooltipText: t('submissionPreview.tooltip.sendToSign'),
      icon: <SendIcon />,
      type: 'footer',
      color: 'primary',
      isButtonEnabled: isEnabled,
      onClick: handleSendToSign,
    },
  ];

  const signConfig = (isEnabled, sub) => {
    const actions = [];

    if (!RoutePath.isUmaRoute(location.pathname)) {
      actions.push({
        text: t('back.to.draft'),
        tooltipText: t('submissionPreview.tooltip.sign'),
        icon: <UndoIcon />,
        type: 'body',
        color: 'secondary',
        isButtonEnabled: isEnabled,
        onClick: () => handleBackToStatus(SubmissionStatus.DRAFT),
      });
    }

    actions.push({
      text: t('submission.sign'),
      tooltipText: t('submissionPreview.tooltip.sign'),
      icon: <SignIcon />,
      type: 'footer',
      color: 'primary',
      isButtonEnabled: isEnabled,
      onClick: () => handleSignButton(sub),
    });

    return actions;
  };

  const sendConfig = isEnabled => [
    {
      text: t('back.to.draft'),
      tooltipText: t('submissionPreview.tooltip.send'),
      icon: <DoubleUndo />,
      type: 'body',
      color: 'secondary',
      isButtonEnabled: isEnabled,
      onClick: () => handleBackToStatus(SubmissionStatus.DRAFT),
    },
    {
      text: t('back.to.ready.to.sign'),
      tooltipText: t('submissionPreview.tooltip.send'),
      icon: <UndoIcon />,
      type: 'body',
      color: 'secondary',
      isButtonEnabled: isEnabled,
      onClick: () => handleBackToStatus(SubmissionStatus.READY_TO_SIGN),
    },
    {
      text: t('actions.send.submission'),
      tooltipText: t('submissionPreview.tooltip.send'),
      icon: <SendIcon />,
      type: 'footer',
      color: 'primary',
      isButtonEnabled: isEnabled,
      onClick: handleSendButton,
    },
  ];

  const sendPaidConfig = isEnabled => [
    {
      text: t('actions.send.submission'),
      tooltipText: t('submissionPreview.tooltip.send'),
      icon: <SendIcon />,
      type: 'footer',
      color: 'primary',
      isButtonEnabled: isEnabled,
      onClick: handleSendButton,
    },
  ];

  const payConfig = isEnabled => [
    {
      text: t('back.to.draft'),
      tooltipText: t('submissionPreview.tooltip.send'),
      icon: <DoubleUndo />,
      type: 'body',
      color: 'secondary',
      isButtonEnabled: isEnabled,
      onClick: () => handleBackToStatus(SubmissionStatus.DRAFT),
    },
    {
      text: t('back.to.ready.to.sign'),
      tooltipText: t('submissionPreview.tooltip.send'),
      icon: <UndoIcon />,
      type: 'body',
      color: 'secondary',
      isButtonEnabled: isEnabled,
      onClick: () => handleBackToStatus(SubmissionStatus.READY_TO_SIGN),
    },
    {
      text: t('actions.submission.proceed'),
      tooltipText: t('submissionPreview.tooltip.pay'),
      icon: <CreditCard />,
      type: 'footer',
      color: 'primary',
      isButtonEnabled: isEnabled,
      onClick: handlePayButton,
    },
  ];

  const sentConfig = () => [
    {
      text: t('actions.submission.request.receipt.preview'),
      tooltipText: t('actions.submission.request.receipt.preview'),
      icon: <SendIcon />,
      type: 'body',
      color: 'primary',
      isButtonEnabled: procedure.confirmationRequestEnabled,
      onClick: handleRequestReceiptButton,
    },
  ];

  const loadSignature = useCallback(() => {
    setPreviewObject({ id: 'signature', path: null });
  }, []);

  const loadSubmissionFile = useCallback(() => {
    (async () => {
      const filePath = await callApi(draftApi.openSubmission)(submission.id);
      setPreviewObject({ id: 'submission', path: filePath });
    })();
  }, [submission?.id]);

  const topEntries = [
    ...(submission?.status === SubmissionStatus.READY_TO_SEND.id
      ? [
        {
          id: 'signature',
          title: t('submission.signatures'),
          subtitle: ' ',
          handleEntryClick: loadSignature,
        },
      ]
      : []),
    {
      id: 'submission',
      title: t('submission.file'),
      subtitle: ' ',
      handleEntryClick: loadSubmissionFile,
    },
  ];

  const loadAttachmentFileMemo = useCallback(
    (file) => {
      (async () => {
        const attachmentFile = await loadAttachmentFile(file, submission);
        setPreviewObject(attachmentFile);
      })();
    },
    [submission?.id],
  );

  const setEachButtonsConfig = useCallback(
    (config) => {
      if (isLargeOrBigger) {
        setBodyButtonsConfig(config);
      } else {
        setBodyButtonsConfig(config.filter(b => b.type === 'body'));
        setFooterButtonsConfig(config.filter(b => b.type === 'footer'));
      }
    },
    [isLargeOrBigger],
  );

  useEffect(() => {
    if (submissionId && !fetchingSubmission && submission == null) {
      fetchSubmission();
    }
  }, [submissionId, submission, i18n.language]);

  useEffect(() => {
    (async () => {
      const procedureName = getProcedureName(submission);
      if (!procedureName && submission) {
        throw new Error(`Unexpected procedure name: ${submission?.procedure}`);
      }
      const isValid = await callApi(draftApi.validateSubmission)(submissionId, false);

      switch (submission?.status) {
        case SubmissionStatus.DRAFT.id:
          if (userData?.roles.includes(Roles.SUBMISSION_DRAFTER)) {
            setEachButtonsConfig(sendToSignConfig(isValid));
            setStaticText(
              t(
                [`submissionPreview.title.sendToSign.${procedureName?.toLowerCase()}`, 'submissionPreview.title.sendToSign'],
                {
                  procedure: t(procedure?.previewLabel || procedure.label),
                },
              ),
            );
          } else if (userData?.roles.includes(Roles.SUBMISSION_SIGNER)) {
            setEachButtonsConfig(sendToSignConfig(isValid));
            setStaticText(
              t('submissionPreview.title.sign', { procedure: t(procedure?.previewLabel || procedure.label) }),
            );
          } else if (userData?.roles.includes(Roles.SUBMISSION_SENDER)) {
            setEachButtonsConfig(sendConfig(false));
            setStaticText(
              t('submissionPreview.title.send', { procedure: t(procedure?.previewLabel || procedure.label) }),
            );
          }
          break;

        case SubmissionStatus.READY_TO_SIGN.id:
          if (userData?.roles.includes(Roles.SUBMISSION_SIGNER)) {
            setEachButtonsConfig(signConfig(true, submission));
            setStaticText(
              t('submissionPreview.title.sign', { procedure: t(procedure?.previewLabel || procedure.label) }),
            );
          } else if (userData?.roles.includes(Roles.SUBMISSION_SENDER)) {
            setEachButtonsConfig(sendConfig(false));
            setStaticText(
              t('submissionPreview.title.send', { procedure: t(procedure?.previewLabel || procedure.label) }),
            );
          }
          break;

        case SubmissionStatus.READY_TO_SEND.id:
          if (userData?.roles.includes(Roles.SUBMISSION_SENDER)) {
            if (submission?.paymentResponse?.successful) {
              setEachButtonsConfig(sendPaidConfig(true));
            } else {
              setEachButtonsConfig(sendConfig(true));
            }

            setStaticText(
              t('submissionPreview.title.send', { procedure: t(procedure?.previewLabel || procedure.label) }),
            );
          }
          break;

        case SubmissionStatus.READY_TO_PAY.id:
          if (userData?.roles.includes(Roles.SUBMISSION_PAYER)) {
            setEachButtonsConfig(payConfig(true));
            setStaticText(
              t('submissionPreview.title.pay', { procedure: t(procedure?.previewLabel || procedure.label) }),
            );
          }
          break;

        case SubmissionStatus.SENT.id:
          if (userData?.roles.includes(Roles.SUBMISSION_SENDER)) {
            setEachButtonsConfig(sentConfig());
            setStaticText(
              t('submissionPreview.title.sent', { procedure: t(procedure?.previewLabel || procedure.label) }),
            );
          }
          break;

        default:
          break;
      }
    })();
  }, [submission, isLargeOrBigger]);

  const isVersionCorrect = isConfigurationVersionCorrect(submission, configurationVersion);

  return {
    submission,
    topEntries,
    bodyButtonsConfig,
    footerButtonsConfig,
    staticText,
    loadSignature,
    loadSubmissionFile,
    previewObject,
    signatureImageUrl,
    handleFacsimileImageError,
    // TO-DO: Move logic to service to test it. No reason to export handlers
    handleSendToSign,
    handleSignButton,
    handleSendButton,
    location,
    history,
    t,
    loadAttachmentFile: loadAttachmentFileMemo,
    dialogSendSubmission: {
      isOpen: isDialogSendSubmissionOpen,
      open: openDialogSendSubmission,
      close: closeDialogSendSubmission,
      confirm: confirmDialogSendSubmission,
    },
    dialogPreviewFacsimile: {
      isOpen: isDialogPreviewFacsimileOpen,
      open: openDialogPreviewFacsimile,
      close: closeDialogPreviewFacsimile,
    },
    dialogStatusChange: {
      isOpen: isStatusChangeDialogOpen,
      confirm: handleStatusChangeDialogConfirm,
      cancel: closeStatusChangeDialog,
      revertStatusAction,
    },
    attachments,
    attachmentsReady: attachmentsConfigReady,
    isLoading,
    isVersionCorrect,
  };
}
